import React, { FunctionComponent, useState, useEffect } from 'react';
import PaperlessBillFormProps, {
  BillingMethod,
  BillMethodOptions,
} from './PaperlessBillForm.types';
import {
  Grid,
  Typography,
  Radio,
  FormControlLabel,
  FormControl,
  RadioGroup,
  makeStyles,
  Theme,
  Card,
  CardContent,
} from '@material-ui/core';
import { useTranslation } from '../../hooks/useTranslation';
import PGEButton from '../buttons';
import ActionNotAvailable from './ActionNotAvailable';
import Ineligible from './Ineligible';
import PhoneTextField from '../phone-text-field';
import useFormState, { convertValidationRules } from '../../hooks/useFormState';
import { validatePhone } from '../../util/form-validation';
import Dropdown from '../dropdown';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import ManageAlertsConfirmPhoneDialog from '../account-summary/manage-alerts/ManageAlertsConfirmPhoneDialog';
import { navigate } from 'gatsby';
import routes from '../../routes';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import { useApolloClient } from '@apollo/react-hooks';
import {
  PhoneType,
  SendActivationCodeResponse,
  UpdateTextPhoneInput,
  UpdateTextPhoneResponse,
  UpdateValidatePhoneError,
} from '../../__generated__/pge-types';
import {
  sendActivationCodeMutation,
  updateTextPhoneMutation,
} from '../account-summary/manage-alerts/queries';
import Backdrop from '../backdrop/Backdrop';
import { getUnenrollmentReasons } from './unenrollmentReasons';
import { displayPhoneNumberFormat } from '../../util/format';

const useStyles = makeStyles((theme: Theme) => ({
  strongText: {
    fontWeight: 600,
  },

  paddingBox: {
    paddingTop: '1em',
  },

  formButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

const PaperlessBillForm: FunctionComponent<PaperlessBillFormProps> = props => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { customer } = useAccountCustomer();
  const { wrapWithLoader } = useWrapWithLoader();
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState<boolean>(false);

  const getContactType = () => {
    const phoneNumber = displayPhoneNumberFormat(props.customerPhone);
    return customer?.contactDetails?.filter(
      contact => contact?.contactValue === phoneNumber,
    )[0]?.contactType;
  };

  const phoneTypeMappings = (phoneType: string): PhoneType => {
    switch (phoneType) {
      case 'MOB':
        return PhoneType.Mobile;
      case 'PNP':
        return PhoneType.PrimaryNotificationPhone;
      case 'ALT':
        return PhoneType.Alternate;
      default:
        return PhoneType.Mobile;
    }
  };

  const savedPaperlessPhoneNumber = props.customerPhone?.match(/\d/g)?.join('');
  const mobPhoneNumber = customer?.contactDetails
    ?.find(contact => contact?.contactType === 'MOB')
    ?.contactValue?.match(/\d/g)
    ?.join('');
  const pnpPhoneNumber = customer?.contactDetails
    ?.find(contact => contact?.contactType === 'PNP')
    ?.contactValue?.match(/\d/g)
    ?.join('');

  const paperlessMethod = {
    key: 'PAPERLESS',
    option: t('CHANGE_BILL_PAPERLESS_OPTION'),
    switchOption: t('CHANGE_BILL_PAPERLESS_SWITCH_OPTION'),
  };

  const mailMethod = {
    key: 'MAIL',
    option: t('CHANGE_BILL_MAIL_OPTION'),
    switchOption: t('CHANGE_BILL_MAIL_SWITCH_OPTION'),
  };

  if (!props?.encryptedPersonId) {
    return <ActionNotAvailable></ActionNotAvailable>;
  }

  if (!props?.isLoggedInUserOnAccount) {
    return (
      <Ineligible title={t('PAPER_LESS_INELIGIBLE')}>
        {richT('PAPER_LESS_INELIGIBLE_NO_PERSON', {
          ASSISTANCE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
          BIZ_ASSISTANCE_NUMBER: t('CALL_FOR_ASSISTANCE_BIZ_NUMBER'),
        })}
      </Ineligible>
    );
  }

  const [selectedOption, setSelectedOption] = useState<BillMethodOptions>({
    method: props.customerBillMethod.method,
    phoneNumber: props.customerPhone,
  });
  const [showUnEnrollReasonSection, setShowUnEnrollReasonSection] = useState<
    boolean
  >(false);
  const [
    showUpdatePhoneNumberDialog,
    setShowUpdatePhoneNumberDialog,
  ] = useState<boolean>(false);
  const [encryptedActvCode, setEncryptedActvCode] = useState<string>('');

  useEffect(() => {
    setSelectedOption({
      method: props.customerBillMethod.method || '',
      phoneNumber: props.customerPhone,
    });
  }, [props.accountNumber]);

  const getIsPaperlessSubmitDisabled = () => {
    if (selectedOption?.method === props?.customerBillMethod?.method) {
      if (
        selectedOption.method === 'MAIL' &&
        !Boolean(form.values.unEnrollReason)
      ) {
        return true;
      }
      if (
        selectedOption.method === 'PAPERLESS' &&
        Boolean(form.errors.phoneNumber)
      ) {
        return true;
      }
      if (
        selectedOption.method === 'PAPERLESS' &&
        props.customerPhone === form.values.phoneNumber
      ) {
        return true;
      }
    }
    return false;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const method = (event.target as HTMLInputElement).value as BillingMethod;
    setSelectedOption(val => ({
      ...val,
      method,
    }));
    setShowUnEnrollReasonSection(method === 'MAIL' ? true : false);
  };

  const validationRules = convertValidationRules<
    {
      phoneNumber: string;
      unEnrollReason: string;
    },
    {
      t: (translation: string) => string;
    }
  >(() => ({
    phoneNumber: value => (Boolean(value) ? validatePhone(value) : null),
  }));

  const form = useFormState(
    {
      phoneNumber: props.customerPhone,
      unEnrollReason: '',
    },
    {
      validate: validationRules,
      validationContext: { t },
    },
  );

  const triggerAddNewPersonContact = wrapWithLoader(async () => {
    setLoading(true);
    const { data: activationCodeResponse } = await apolloClient.mutate<{
      sendActivationCode: SendActivationCodeResponse;
    }>({
      mutation: sendActivationCodeMutation,
      variables: {
        phoneNumber: form.props('phoneNumber').value,
      },
    });

    setLoading(false);

    const encryptedActivationCode =
      activationCodeResponse?.sendActivationCode?.encryptedActivationCode;

    if (!encryptedActivationCode) {
      return Promise.reject('No encrypted activation code in response');
    } else {
      setEncryptedActvCode(encryptedActivationCode);
      setShowUpdatePhoneNumberDialog(true);
    }
  });

  const handleSubmit = () => {
    const phoneNumber = form.props('phoneNumber').value;
    if (
      [savedPaperlessPhoneNumber, mobPhoneNumber, pnpPhoneNumber].includes(
        phoneNumber,
      ) ||
      !Boolean(phoneNumber)
    ) {
      props.onSubmit({
        method: selectedOption.method,
        phoneNumber,
        unEnrollReason: form.props('unEnrollReason').value,
      });
    } else {
      void triggerAddNewPersonContact();
    }
  };

  const onActivatePhone = async (
    activationCode: string,
    onInvalid: () => void,
  ) => {
    setLoading(true);

    const updatePhonePayload: UpdateTextPhoneInput = {
      encryptedPersonId: customer?.encryptedPersonId || '',
      phoneNumber: '+1' + form.values.phoneNumber,
      activationCodeInput: activationCode,
      encryptedActivationCode: encryptedActvCode,
      isActive: 'true',
      phoneType: phoneTypeMappings(getContactType() as PhoneType),
    };

    const { data: updatePhoneResponse } = await apolloClient.mutate<{
      updateTextPhone: UpdateTextPhoneResponse;
    }>({
      mutation: updateTextPhoneMutation,
      variables: {
        payload: updatePhonePayload,
      },
    });

    setLoading(false);

    if (updatePhoneResponse?.updateTextPhone.success) {
      setShowUpdatePhoneNumberDialog(false);
      props.onSubmit({
        method: selectedOption.method,
        phoneNumber: form.values.phoneNumber,
        unEnrollReason: form.props('unEnrollReason').value,
      });
    } else if (updatePhoneResponse?.updateTextPhone.updatePhoneError) {
      const error = updatePhoneResponse?.updateTextPhone.updatePhoneError;
      if (error === UpdateValidatePhoneError.InvalidCode) {
        return onInvalid();
      } else if (
        error === UpdateValidatePhoneError.ContactExists ||
        error === UpdateValidatePhoneError.PhoneAttachedToActiveAccount
      ) {
        form.setError('phoneNumber', t('MANAGE_ALERTS_BAD_PHONE_NUMBER'));
        return navigate(routes.MANAGE_ALERTS);
      }
    }
  };

  const handleTooManyFailures = () => {
    setShowUpdatePhoneNumberDialog(false);
    setEncryptedActvCode('');
    void navigate(routes.MANAGE_ALERTS);
  };

  return (
    <>
      <Card>
        <CardContent>
          {loading && <Backdrop forceOpen />}
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography component="span">
                <Typography component="span">
                  {props?.customerBillMethod.method === 'PAPERLESS'
                    ? richT('CHANGE_BILL_PAPERLESS_INFO', {
                        CUSTOMER_EMAIL: props.customerEmail,
                      })
                    : richT('CHANGE_BILL_PAPERLESS_MAIL_INFO')}
                </Typography>
                {props?.customerBillMethod.method === 'PAPERLESS' && (
                  <Typography component="span">
                    {richT('CHANGE_BILL_PAPERLESS_INFO_LETS_YOU')}
                  </Typography>
                )}
              </Typography>
            </Grid>
            <Grid item className={classes.paddingBox}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="method"
                  name={'method'}
                  value={selectedOption.method}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={paperlessMethod.key}
                    control={<Radio color="primary" />}
                    label={
                      props?.customerBillMethod.method === paperlessMethod.key
                        ? paperlessMethod.option
                        : paperlessMethod.switchOption
                    }
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={mailMethod.key}
                    control={<Radio color="primary" />}
                    label={
                      props?.customerBillMethod.method === mailMethod.key
                        ? mailMethod.option
                        : mailMethod.switchOption
                    }
                    labelPlacement="end"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {selectedOption.method === 'PAPERLESS' && (
              <Grid item className={classes.paddingBox}>
                <Typography variant="subtitle1">
                  {t('CHANGE_BILL_PAPERLESS_INFO_MOB')}
                </Typography>
                <PhoneTextField
                  label={t('MOBILE_NUMBER_LABEL')}
                  style={{ width: '100%' }}
                  {...form.props('phoneNumber')}
                />
              </Grid>
            )}
            {props?.customerBillMethod?.method !== 'MAIL' &&
              showUnEnrollReasonSection && (
                <Grid item className={classes.paddingBox}>
                  <Dropdown
                    value={form.values.unEnrollReason}
                    style={{ width: '100%' }}
                    label={t('PLEASE_SELECT_AN_OPTION')}
                    selectionList={getUnenrollmentReasons(t)}
                    onChange={async e => {
                      await form.setValue('unEnrollReason', e.target.value);
                    }}
                  />
                </Grid>
              )}
            <Grid container justify="flex-end">
              <PGEButton
                className={classes.formButton}
                variant="contained"
                color="primary"
                disabled={getIsPaperlessSubmitDisabled()}
                type={'submit'}
                onClick={handleSubmit}
              >
                {t('CHANGE')}
              </PGEButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {showUpdatePhoneNumberDialog && (
        <ManageAlertsConfirmPhoneDialog
          onClose={() => setShowUpdatePhoneNumberDialog(false)}
          phoneNumber={form.values.phoneNumber}
          onSubmit={onActivatePhone}
          hasActivationCode={true}
          onTooManyFailures={handleTooManyFailures}
        />
      )}
    </>
  );
};

export default PaperlessBillForm;
